"use server";

import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import {
  clearAttempts,
  createAccount,
  emailProblem,
  getAccountByEmail,
  getCurrentAccount,
  endSession,
  passwordProblem,
  recordFailedAttempt,
  startSession,
  tooManyAttempts,
  verifyPassword,
} from "@/lib/auth";
import { claimSeller, listSellersByAccount } from "@/lib/repo";
import { clearBoothCookie, setBoothCookie } from "@/lib/session";

export type AuthState = { error?: string };

/**
 * Only same-site paths are honoured as a return target.
 *
 * `next` arrives from the query string, so anything absolute or protocol
 * relative would turn sign-in into an open redirect pointing at someone else's
 * site with our name on the link.
 */
function safeNext(value: FormDataEntryValue | null): string | null {
  const next = typeof value === "string" ? value.trim() : "";
  if (!next.startsWith("/") || next.startsWith("//")) return null;
  return next;
}

/** Sends the user wherever makes sense given how many booths they own. */
async function goToBooth(accountId: number, next?: string | null): Promise<never> {
  const mine = listSellersByAccount(accountId);
  if (mine.length === 1) {
    await setBoothCookie(mine[0].id);
    redirect(next ?? "/");
  }
  // Several collections: they have to choose which one is acting first, so the
  // return target rides along on the picker.
  redirect(next ? `/booths?next=${encodeURIComponent(next)}` : "/booths");
}

export async function signUpAction(
  _prev: AuthState,
  formData: FormData,
): Promise<AuthState> {
  const email = String(formData.get("email") ?? "");
  const password = String(formData.get("password") ?? "");

  const emailErr = emailProblem(email);
  if (emailErr) return { error: emailErr };
  const pwErr = passwordProblem(password);
  if (pwErr) return { error: pwErr };

  if (getAccountByEmail(email)) {
    return { error: "There's already an account with that email. Sign in instead." };
  }

  const account = createAccount(email, password);
  await startSession(account.id);
  revalidatePath("/", "layout");
  return goToBooth(account.id, safeNext(formData.get("next")));
}

export async function signInAction(
  _prev: AuthState,
  formData: FormData,
): Promise<AuthState> {
  const email = String(formData.get("email") ?? "");
  const password = String(formData.get("password") ?? "");

  if (tooManyAttempts(email)) {
    return { error: "Too many attempts. Wait a few minutes and try again." };
  }

  const account = getAccountByEmail(email);
  // One message for both "no such account" and "wrong password", so sign-in
  // can't be used to find out which emails are registered.
  const generic = { error: "Email or password is wrong." };

  if (!account || !verifyPassword(password, account.password_hash)) {
    recordFailedAttempt(email);
    return generic;
  }

  clearAttempts(email);
  await startSession(account.id);
  revalidatePath("/", "layout");
  return goToBooth(account.id, safeNext(formData.get("next")));
}

export async function signOutAction(): Promise<void> {
  await endSession();
  await clearBoothCookie();
  revalidatePath("/", "layout");
  redirect("/signin");
}

/** Takes ownership of a booth that predates accounts. */
export async function claimBoothAction(formData: FormData): Promise<void> {
  const account = await getCurrentAccount();
  if (!account) redirect("/signin");

  const id = Number(formData.get("seller_id"));
  if (Number.isInteger(id) && claimSeller(id, account.id)) {
    await setBoothCookie(id);
  }
  revalidatePath("/", "layout");
  redirect("/");
}
